<template>
    <div class="">
        <slot>
            <div class="btn-group-toggle draw-interactions text-center w-100"
                :class="{'btn-group': groupControls}"
                aria-label="Draw Controls">
                <label
                    v-for="control in drawControls"
                    :key="`control-${id}-${control.id}`"
                    :for="`control-${id}-${control.id}`"
                    :title="control.label"
                    :class="[
                        {'active': control.id === drawControlId},
                        {'disabled': isControlDisabled(control.id) },
                        control.class ? control.class : '']"
                    class="btn btn-outline-main min-w-auto">
                    <input type="radio"
                        :name="`toogle-draw-${id}`"
                        :id="`control-${id}-${control.id}`"
                        :value="control.id"
                        v-model="drawControlId"
                        :disabled="isControlDisabled(control.id)"
                        autocomplete="off">
                    <img v-if="control.image" :src="assetsPathModule(control.image)">
                    <i v-else-if="control.icon" :class="control.icon"></i>
                    <span v-else> {{ control.label }} </span>
                </label>
            </div>
            <div class="row">
                <div class="col">
                    <ul class="small pl-3 mb-0">
                        <li>
                            <code>Double Click</code> para terminar o desenho
                        </li>
                        <li>
                            <code>Snap</code> activo enquanto desenha {{ enableModify ? 'ou modifica' : ''}}
                        </li>
                        <li v-if="showSelect">
                            Selecione a geometria para <code>modificar</code>
                        </li>
                        <li v-if="showSelect">
                            <code>Delete</code> para eliminar geometria
                        </li>
                        <li v-if="enableModify">
                            <code>Alt+Click</code> para apagar vértice
                        </li>
                        <li v-if="showHelpLimit">
                            <span class="">{{ `Possivel adicionar no mínimo ${minFeatures} e máximo ${maxFeatures} elementos.` }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </slot>
    </div>
</template>

<script>
    import Draw, { createBox, createRegularPolygon } from "ol/interaction/Draw";
    import { Select, Modify, Snap } from "ol/interaction";
    import { fromCircle } from "ol/geom/Polygon";
    import { Circle } from "ol/geom";
    import VectorLayer from 'ol/layer/Vector';
    import { Vector as VectorSource } from 'ol/source';
    import { createEditingStyle } from 'ol/style/Style';
    import { extend as extendArray } from 'ol/array';
    import VectorEventType from 'ol/source/VectorEventType';
    import Collection from 'ol/Collection';
    import { capitalizeFirstLetterWord } from '@config/utils';

    let varsOriginal = {
        interactions: {
            draw: null,
            select: null,
            modify: null,
            snap: null,
            alphanumeric: null
        },
        vectorLayer: null,
    };
    let vars = Object.assign({}, varsOriginal);

    export default {
        name: 'DrawInteraction',
        mixins: [],
        props: {
            id: {
                type: String,
                require: true,
            },
            moduleCode: {
                type: String,
                require: true,
            },
            value: {
                type: Array,
                default: ()=> [],
            },
            isActive: {
                type: Boolean,
                default: true
            },
            show: {
                type: Boolean,
                default: false
            },
            mapClass: {
                type: Object,
                require: true,
            },
            drawTypes: {
                type: [String, Array],
                required: true,
                validator: function (prop) {
                    let valid = [
                        'point',
                        'text',
                        'line',
                        'freehand',
                        'freehand-shape',
                        'ellipse',
                        'circle',
                        'polygon',
                        'rectangle',
                        'square',
                    ];
                    if(typeof prop === "string"){
                        return valid.includes(prop);
                    }
                    return prop.every(e => valid.includes(e));
                }
            },
            showLabel: {
                type: Boolean,
                default: false
            },
            showSelect: {
                type: Boolean,
                default: false
            },
            showAlphanumeric: {
                type: Boolean,
                default: false
            },
            showClear: {
                type: Boolean,
                default: false
            },
            showHelpLimit: {
                type: Boolean,
                default: false
            },
            minFeatures: {
                type: Number,
                default: 0
            },
            maxFeatures: {
                type: Number,
                default: 1
            },
            enableModify: {
                type: Boolean,
                default: true
            },
            groupControls: {
                type: Boolean,
                default: true
            },
        },
        data() {
            return {
                loaded: false,
                className: 'ol-draw',
                valueCopy: [],
                drawFeatures: new Collection(),
                drawControlId: undefined,
                availableDrawTypes: [
                    {
                        id: 'point',
                        label: 'Ponto',
                        type: 'Point',
                        icon: 'fas fa-circle',
                        image: '',
                        options:{},
                    },
                    {
                        id: 'text',
                        label: 'Texto',
                        type: 'Text',
                        icon: 'fas fa-circle',
                        image: '',
                        options:{},
                    },
                    {
                        id: 'line',
                        label: 'Linha',
                        type: 'LineString',
                        icon: 'fas fa-slash',
                        image: '',
                        options:{}},
                    {
                        id: 'freehand',
                        label: 'Freehand',
                        type: 'LineString',
                        icon: 'fas fa-signature',
                        image: '',
                        options:{
                            freehand: true
                        },
                    },
                    {
                        id: 'freehand-shape',
                        label: 'Freehand Shape',
                        type: 'Polygon',
                        icon: 'fas fa-cloud',
                        image: '',
                        options:{
                            freehand: true
                        },
                    },
                    {
                        id: 'ellipse',
                        label: 'Ellipse',
                        type: 'Circle',
                        icon: 'far fa-eye',
                        image: '',
                        options:{
                            geometryFunction: function(coordinates, geometry) {
                                var center = coordinates[0];
                                var last = coordinates[1];
                                var dx = center[0] - last[0];
                                var dy = center[1] - last[1];
                                var radius = Math.sqrt(dx * dx + dy * dy);
                                var circle = new Circle(center, radius);
                                var polygon = fromCircle(circle, 64);
                                polygon.scale(dx/radius, dy/radius);
                                if (!geometry) {
                                    geometry = polygon;
                                } else {
                                    geometry.setCoordinates(polygon.getCoordinates());
                                }
                                return geometry;
                            },
                        },
                    },
                    {
                        id: 'circle',
                        label: 'Círculo',
                        type: 'Circle',
                        icon: 'far fa-circle',
                        image: '',
                        options:{},
                    },
                    {
                        id: 'polygon',
                        label: 'Polígono',
                        type: 'Polygon',
                        icon: 'fas fa-vector-square',
                        image: '',
                        options:{},
                    },
                    {
                        id: 'rectangle',
                        label: 'Rectângulo',
                        type: 'Circle',
                        icon: 'far fa-square',
                        image: '',
                        options:{
                            maxPoints: 2,
                            geometryFunction: createBox(),
                        },
                    },
                    {
                        id: 'square',
                        label: 'Quadrado',
                        type: 'Circle',
                        icon: 'far fa-square',
                        image: '',
                        options:{
                            geometryFunction: createRegularPolygon(4),
                        },
                    },
                ],
                selectControl: {
                    id: 'select',
                    type: 'Select',
                    icon: 'fas fa-mouse-pointer',
                    image: '',
                    label: 'Selecionar',
                    // class:'txt-error'
                },
                alphanumericControl: {
                    id: 'alphanumeric',
                    type: 'Alphanumeric',
                    icon: 'fas fa-file-alt',
                    image: '',
                    label: 'Formulário',
                    // class:'txt-error'
                },
                clearControl: {
                    id: 'clear',
                    type: 'Clear',
                    icon: 'fas fa-trash',
                    image: '',
                    label: 'Limpar',
                    class:'txt-error'
                },
            }
        },
        computed: {
            configMap() {
                return this.moduleCode ? this.$store.getters.getConfigMap(this.moduleCode) : {};
            },
            defaultStyleMap() {
                return this.moduleCode ? this.$store.getters.getDefaultStyleMap(this.moduleCode) : {};
            },
            editingStyleMap() {
                return this.moduleCode ? this.$store.getters.getEditingStyleMap(this.moduleCode) : {};
            },
            drawControls() {
                let types = [...this.drawTypes];
                let controls = this.availableDrawTypes.filter(d => types.includes(d.id));
                if(controls.length>0) {
                    if(this.showSelect){
                        controls.splice(0, 0, this.selectControl);
                    }
                    if(this.showAlphanumeric){
                        controls.push(this.alphanumericControl);
                    }
                    if(this.showClear){
                        controls.push(this.clearControl);
                    }
                }
                return controls;
            },
            editControls() {
                let editControls = [this.selectControl.id, this.clearControl.id];
                if(this.alphanumericControl) {
                    editControls.push(this.alphanumericControl.id);
                }
                return editControls;
            },
            features(){
                return this.drawFeatures;
            },
            featuresList(){
                return this.features.getArray();
            },
            numFeatures(){
                return this.featuresList.length;
            },
            isLimitFeatures(){
                return this.maxFeatures <= this.numFeatures;
            },
        },
        beforeMount () {
        },
        mounted() {
            // Here we are following the Vue2 convention on custom v-model:
            // https://github.com/vuejs/vue/issues/2873#issuecomment-223759341
            this.copyValue(this.value);
        },
        created() {
            this.initialize();
        },
        beforeDestroy() {
            this.reset();
        },
        methods: {
            initialize: function(){
                if(this.createLayer()){
                    this.addBaseInteractions();
                }
            },
            reset: function(){
                this.removeInteractions();
                this.resetControl();
                let layer = this.layer();
                if(layer){
                    if(this.mapClass){
                        this.mapClass.removeLayer(layer);
                        layer = null;
                    }
                }
                Object.assign(this.$data.drawFeatures, this.$options.data().drawFeatures);
                vars = Object.assign({}, varsOriginal);
            },
            resetControl: function(){
                this.drawControlId=null;
                this.setEventListener(false);
            },
            copyValue (value) {
                this.valueCopy = value;
                this.syncModel();
            },
            syncModel: function(){
                function comparer(otherArray){
                    return function(current){
                        return otherArray.filter(other => other === current).length == 0;
                    }
                }
                let features = this.featuresList;
                let toAdd = this.valueCopy.filter(comparer(features));
                let toRemove = features.filter(comparer(this.valueCopy));

                let layer = this.layer();
                let source = layer?.getSource();
                toRemove.forEach( f => {
                    if(source && source.hasFeature(f)){
                        source.removeFeature(f);
                    }else{
                        this.drawFeatures.remove(f);
                    }
                });
                toAdd.forEach( f => {
                    if(source && !source.hasFeature(f)){
                        source.addFeature(f);
                    }else{
                        this.drawFeatures.push(f);
                    }
                })
            },
            handleCreateLayer: function(evt) {
                this.$emit('onCreateLayer', vars.vectorLayer);
            },
            handleFeatures: function(evt) {
                this.updateInteractionsState();
                this.$emit('input', [...this.featuresList]);
                this.$emit(`on${capitalizeFirstLetterWord(evt.type)}`, {feature: evt.feature, features: [...this.featuresList]});
            },
            handleModify: function(evt) {
                this.$emit('onModify', evt.features ? evt.features.getArray() : []);
            },
            handleSelect: function(evt) {
                let style = null;
                let interactions = vars.interactions;
                if(interactions.select){
                    style = interactions.select.getStyle();
                }
                this.$emit('onSelect', {
                    'selected': evt.selected,
                    'deselected': evt.deselected,
                    'style': style
                });
            },
            handleAlphanumericSelect: function(evt) {
                this.$emit(`onAlphanumeric`, {
                    'feature': evt.selected.length ? evt.selected[0] : null,
                    'deselect': (feat) => {this.deselectFeature(feat);}
                });
            },
            layer: function(){
                return vars.vectorLayer;
            },
            createLayer: function(){
                let layer = this.layer();
                if(!layer){
                    let config = this.configMap.layers.draw;
                    let source = new VectorSource({
                        useSpatialIndex: false,
                        wrapX: config.wrapX !== undefined ? config.wrapX : false, //Wrap the world horizontally on the sketch overlay.
                        features: this.features,
                    });
                    source.on(VectorEventType.ADDFEATURE, (evt) => {
                        // let _source = evt.target;
                        let _feature = evt.feature;
                        _feature.setProperties({'_geom': _feature.getGeometry().getType().toLowerCase()});
                        // if(!_feature.getStyle() && !_feature.getStyleFunction()) {
                        //     _feature.setStyle(layer.getStyle());
                        // }
                        this.handleFeatures(evt);
                    });
                    source.on(VectorEventType.REMOVEFEATURE, (evt) => {
                        this.handleFeatures(evt);
                    });
                    let vectorLayer = new VectorLayer({
                        source: source,
                        visible: true,
                        zIndex: config.zindex,
                        style: config.style ? config.style : this.defaultStyleMap,
                    });
                    this.mapClass.addLayer(vectorLayer);
                    layer = vars.vectorLayer = vectorLayer;
                    this.handleCreateLayer();
                }
                return layer;
            },
            zoomToLayer: function(){
                let layer = this.layer();
                if(layer){
                    let source = layer.getSource();
                    if(source){
                        let featExtent = source.getExtent();
                        if (featExtent) {
                            this.mapClass.fitToExtent(featExtent);
                        }
                    }
                }
            },
            setInteraction: function(id, oldId) {
                this.removeDrawInteractions();
                this.disableAllInteractions(false);

                let drawControl = this.drawControls.find(c => c.id === id);
                if(drawControl){
                    switch (drawControl.type.toLowerCase()) {
                        case this.selectControl.id:
                            this.setActiveSelectInteraction(true);
                            break;
                        case this.alphanumericControl.id:
                            this.setActiveAlphanumericInteraction(true);
                            break;
                        case this.clearControl.id:
                            this.clearFeatures();
                            if(oldId && id != oldId){
                                this.$nextTick(() => {
                                    this.drawControlId = oldId;
                                });
                            }
                            break;
                        default:
                            if(!this.isLimitFeatures){
                                this.addDrawInteractions(drawControl);
                            }
                            // this.showMessage('');
                    }
                }

                this.updateUI();
            },
            removeInteractions: function(keys='all'){
                let interactions = vars.interactions;
                for (let key in interactions) {
                    if(keys == 'all' || keys.includes(key)){
                        let interaction = interactions[key];
                        if(interaction){
                            if(this.mapClass){
                                this.mapClass.removeInteraction(interaction);
                            }
                            interactions[key] = null;
                        }
                    }
                }
                this.updateUI();
            },
            addDrawInteractions: function(drawControl){
                this.setActiveBaseInteractions(false);

                let config = this.configMap?.interactions?.draw ?? {};
                let features = this.features;
                // let source = this.layer.getSource();
                // DRAW
                let draw = new Draw({
                    style: config?.style ?? this.editingStyleMap,
                    // source: source,
                    features: features,
                    type: drawControl.type,
                    ...drawControl.options,
                });
                this.mapClass.addInteraction(draw);
                vars.interactions.draw = draw;

                // SNAP
                this.setActiveSnapInteraction(true);
            },
            removeDrawInteractions: function(){
                this.removeInteractions(['draw', 'snap']);
            },
            disableAllInteractions: function(reset=true){
                let interactions = vars.interactions;
                for (let key in interactions) {
                    let interaction = interactions[key];
                    if(interaction){
                        interaction.setActive(false);
                    }
                }
                if(reset){
                    this.resetControl();
                }
            },
            addBaseInteractions: function(){
                // SELECT
                this.createSelectInteraction();
                // MODIFY
                this.createModifyInteraction();
                // ALPFANUMERIC
                if(this.showAlphanumeric) {
                    this.createAlphanumericInteraction();
                }
            },
            createSelectInteraction: function(){
                if(this.showSelect){
                    let interactions = vars.interactions;
                    if(!interactions.select){
                        let select = new Select({
                            wrapX: false,
                            multi: false,
                            style: this.getDefaultStyleFunction()
                        });
                        select.setActive(false);
                        select.on('select', (evt) => {
                            this.handleSelect(evt);
                        });
                        select.on('change:active', function (evt) {
                            if(evt.oldValue == true){
                                const selectedFeatures = evt.target.getFeatures();
                                if(selectedFeatures.getLength()>0){
                                    let deselected = [];
                                    for (let i = selectedFeatures.getLength() - 1; i >= 0; --i) {
                                        const feature = selectedFeatures.item(i);
                                        selectedFeatures.remove(feature);
                                        deselected.push(feature);
                                    }
                                    evt.target.dispatchEvent({
                                        type: 'select',
                                        selected: [],
                                        deselected: deselected
                                    });
                                }
                            }
                        });
                        this.mapClass.addInteraction(select);
                        interactions.select = select;
                    }
                }
            },
            createModifyInteraction: function(){
                if(this.enableModify){
                    let interactions = vars.interactions;
                    if(!interactions.modify){
                        let modify = new Modify({
                            features: this.showSelect && interactions.select ?
                                        interactions.select.getFeatures() : this.features,
                            // // the SHIFT key must be pressed to delete vertices, so
                            // // that new vertices can be drawn at the same position
                            // // of existing vertices
                            // deleteCondition: function(mapBrowserEvent) {
                            //     return shiftKeyOnly(mapBrowserEvent) &&
                            //         singleClick(mapBrowserEvent);
                            //      return altKeyOnly(mapBrowserEvent) &&
                            //         singleClick(mapBrowserEvent);
                            // },
                        });
                        modify.on('modifyend', (evt) => {
                            this.handleModify(evt);
                        });
                        modify.setActive(!this.showSelect);
                        this.mapClass.addInteraction(modify);
                        interactions.modify = modify;
                    }
                }
            },
            createAlphanumericInteraction: function(){
                if(this.showAlphanumeric){
                    let interactions = vars.interactions;
                    if(!interactions.alphanumeric){
                        let select = new Select({
                            wrapX: false,
                            multi: false,
                            style: this.getDefaultStyleFunction()
                        });
                        select.setActive(false);
                        select.on('select', (evt) => {
                            this.handleAlphanumericSelect(evt);
                        });
                        select.on('change:active', function (evt) {
                            // if(evt.oldValue == true){
                            //     const selectedFeatures = evt.target.getFeatures();
                            //     if(selectedFeatures.getLength()>0){
                            //         let deselected = [];
                            //         for (let i = selectedFeatures.getLength() - 1; i >= 0; --i) {
                            //             const feature = selectedFeatures.item(i);
                            //             selectedFeatures.remove(feature);
                            //             deselected.push(feature);
                            //         }
                            //         evt.target.dispatchEvent({
                            //             type: 'select',
                            //             selected: [],
                            //             deselected: deselected
                            //         });
                            //     }
                            // }
                        });
                        this.mapClass.addInteraction(select);
                        interactions.alphanumeric = select;
                    }
                }
            },
            setActiveSnapInteraction: function(flag){
                flag = !!flag;
                // The snap interaction must be added after the Modify and Draw interactions
                // in order for its map browser event handlers to be fired first. Its handlers
                // are responsible of doing the snapping.
                let interactions = vars.interactions;
                if(interactions.snap){
                    this.removeInteractions(['snap']);
                }
                if(flag) {
                    let features = this.features;
                    // SNAP
                    let snap= new Snap({
                        // source: source,
                        features: features,
                    });
                    snap.setActive(flag);
                    this.mapClass.addInteraction(snap);
                    vars.interactions.snap = snap;
                }
            },
            setActiveSelectInteraction: function(flag){
                flag = !!flag;
                if(this.showSelect){
                    let control = vars.interactions[this.selectControl.id];
                    if(control){
                        control.setActive(flag);
                    }
                    this.setActiveModifyInteraction(flag);
                }
            },
            setActiveModifyInteraction: function(flag){
                flag = !!flag;
                if(this.enableModify){
                    let control = vars.interactions['modify'];
                    if(control){
                        control.setActive(flag);
                        this.setEventListener(flag);
                    }
                    this.setActiveSnapInteraction(flag);
                }
            },
            setActiveAlphanumericInteraction: function(flag){
                flag = !!flag;
                if(this.showAlphanumeric){
                    let control = vars.interactions[this.alphanumericControl.id];
                    if(control){
                        control.setActive(flag);
                    }
                    this.setActiveBaseInteractions(false);
                }
            },
            setActiveBaseInteractions: function(flag){
                if(this.showSelect){
                    this.setActiveSelectInteraction(!!flag);
                }
            },
            updateInteractionsState: function() {
                let controls = this.drawControls.filter(c => !this.editControls.includes(c.id));
                if(!controls.find(c => !this.isControlDisabled(c.id))){
                    this.removeDrawInteractions();
                }
                // this.updateUI();
            },
            setEventListener: function(flag){
                flag = !!flag;
                if(flag){
                    document.removeEventListener('keydown', this.deleteHandler);
                    document.addEventListener('keydown', this.deleteHandler);
                }else{
                    document.removeEventListener('keydown', this.deleteHandler);
                }
            },
            deleteHandler: function(evt) {
                if(evt instanceof KeyboardEvent) {
                    if (evt.key === 'Delete') {
                        let interation = vars.interactions.select;
                        if(interation) {
                            let features = this.features;
                            let selectedFeatures = interation.getFeatures();
                            selectedFeatures.forEach( f => {
                                features.remove(f);
                            });
                            selectedFeatures.clear();
                        }
                    }
                }
            },
            getDefaultStyleFunction: function() {
                const styles = createEditingStyle();
                extendArray(styles['Polygon'], styles['LineString']);
                extendArray(styles['GeometryCollection'], styles['LineString']);

                return function (feature) {
                    if (!feature.getGeometry()) {
                        return null;
                    }
                    return styles[feature.getGeometry().getType()];
                };
            },
            deselectFeature: function(feature) {
                if(feature) {
                    [vars.interactions.select, vars.interactions.alphanumeric]
                    .filter(n => n)
                    .forEach(interation => {
                        let selectedFeatures = interation.getFeatures();
                        selectedFeatures.remove(feature);
                    });
                }
            },
            clearFeatures: function(){
                let layer = this.layer();
                if(layer){
                    let source = layer.getSource();
                    if(source){
                        let features = source.getFeatures();
                        if(this.showSelect){
                            let interactions = vars.interactions;
                            if(interactions.select){
                                let selectedFeatures = interactions.select.getFeatures();
                                if(selectedFeatures.getLength() > 0) {
                                    features = selectedFeatures.getArray();
                                }
                            }
                        }
                        for (let i = 0; i < features.length; i++) {
                            source.removeFeature(features[i])
                        }
                    }
                }
            },
            isControlDisabled: function(id) {
                let disabled = this.editControls.includes(id) && this.numFeatures == 0
                    || this.isLimitFeatures && !this.editControls.includes(id);

                // if(disabled) {
                //     let interactions =  vars.interactions;
                //     let interaction = interactions[id];
                //     if(interaction && interaction.getActive()){
                //         interaction.setActive(false);
                //     }
                // }

                return disabled;
            },
            forceUpdateComponent() {
                this.updateUI();
                this.resetControl();
            },
            updateUI: function() {
                this.$forceUpdate();
            },
        },
        watch: {
            value (value) {
                // This watch works from the code side of the 2-way-binding:
                this.copyValue(value);
            },
            show: {
                immediate: false,
                handler(value, oldVal) {
                    if(value){
                        this.initialize();
                    }else{
                        this.reset();
                    }
                },
            },
            drawControlId (value, oldValue) {
                this.setInteraction(value, oldValue);
            },
            isActive: {
                immediate: false,
                handler(value, oldVal) {
                    if(value){
                        this.forceUpdateComponent();
                        this.setActiveBaseInteractions();
                    }else{
                        this.disableAllInteractions();
                    }
                }
            },
        },

    }
</script>

<style lang="scss" scoped>
    .btn-group-toggle.draw-interactions {
        .btn:not(.btn-toggle) {
            font-size: .9rem;
        }
    }
</style>